"use client";

import { Button } from "@/components/ui/Button";
import { Input, ToggleButton } from "@/components/ui/input";
import PhoneNumber from "@/components/ui/input/PhoneNumber";
import Modal from "@/components/ui/Modal";
import { cdnCommonPath } from "@/constants/cdnUrls";
import { CURRENCY_CODES } from "@/constants/currency";
import { useAuth } from "@/contexts/AuthContext";
import { useModal, useModalHelpers } from "@/contexts/ModalContext";
import {
  PointConvertProvider,
  usePointConvert,
} from "@/contexts/PointConvertContext";
import { usePriceFormatter } from "@/hooks/usePriceFormatter";
import { useTimer } from "@/hooks/useTimer";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { pointConvertService } from "@/services/pointConvertService";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { YouTubeEmbed } from "@next/third-parties/google";
import clsx from "clsx";
import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import CreditCardForm from "../payment/common/CreditCardForm";
import ThreeDPaymentModal from "../payment/common/ThreeDPaymentModal";
import SavedCardsContent from "./SavedCardsContent";

const STORAGE_KEY = "pointConvertFormData";

const getLocalPhoneNumber = (val) => {
  if (!val) return "";
  const parts = String(val).split("-");
  if (parts.length < 2) return "";
  return parts.slice(1).join("").replace(/\D/g, "");
};

const ApplicationModalContent = ({
  modal,
  step,
  setStep,
  t,
  paymentId,
  periodConversion,
  setSelectedSavedCard,
  selectedSavedCard,
}) => {
  const [isSubmittingStep1, setIsSubmittingStep1] = useState(false);
  const [isSubmittingStep2, setIsSubmittingStep2] = useState(false);
  const [isResendingOTP, setIsResendingOTP] = useState(false);
  const [formData, setFormData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [isSubmittingStep3, setIsSubmittingStep3] = useState(false);

  const [inlineError, setInlineError] = useState("");

  const timer = useTimer({
    initialTime: 180,
    direction: "down",
  });

  const clearInlineError = () => setInlineError("");

  const setServiceError = (payload) => {
    if (!payload) return;

    const type = payload?.Type;
    const name = payload?.Name;

    setInlineError(type && name ? t(`${type}.${name}`) : t("common.error"));
  };

  const applicationSchema = useMemo(
    () =>
      z.object({
        cardName: z
          .string()
          .min(1, t("common.required"))
          .min(2, t("common.minLength")),
        phone: z
          .string()
          .min(1, t("common.required"))
          .refine(
            (val) => {
              const local = getLocalPhoneNumber(val);
              return local.length >= 6 && local.length <= 15;
            },
            { message: t("common.invalidPhone") },
          ),
      }),
    [t],
  );

  const otpSchema = useMemo(
    () =>
      z.object({
        otpCode: z
          .string()
          .min(1, t("common.required"))
          .length(6, t("common.otpLength"))
          .regex(/^[A-Za-z0-9]+$/, t("common.otpFormat")),
      }),
    [t],
  );

  const applicationForm = useForm({
    resolver: zodResolver(applicationSchema),
    defaultValues: { cardName: "", phone: "" },
  });

  const otpForm = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: { otpCode: "" },
  });

  const {
    control: applicationControl,
    handleSubmit: handleApplicationSubmit,
    formState: { errors: applicationErrors },
    reset: resetApplicationForm,
  } = applicationForm;

  const {
    control: otpControl,
    handleSubmit: handleOtpSubmit,
    formState: { errors: otpErrors },
    reset: resetOtpForm,
  } = otpForm;

  useEffect(() => {
    if (modal.isOpen && step === 2) timer.restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.isOpen, step]);

  useEffect(() => {
    if (!modal.isOpen) {
      setStep(1);
      resetApplicationForm();
      resetOtpForm();
      timer.reset();
      setIsSubmittingStep1(false);
      setIsSubmittingStep2(false);
      setIsResendingOTP(false);
      setFormData(null);
      setInlineError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.isOpen]);

  useEffect(() => {
    setInlineError("");
  }, [step]);

  const onApplicationSubmit = async (data) => {
    clearInlineError();
    setIsSubmittingStep1(true);
    try {
      const response = await pointConvertService.sendSaveCardOTP({
        PhoneNumber: getLocalPhoneNumber(data.phone),
      });

      if (!response?.Message) {
        setFormData(data);
        setStep(2);
        timer.restart();
      } else {
        setServiceError(response);
      }
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsSubmittingStep1(false);
    }
  };

  const onOtpSubmit = async (data) => {
    if (timer.timeLeft <= 0) return;

    clearInlineError();
    setIsSubmittingStep2(true);
    try {
      const response = await pointConvertService.verifySaveCardOTP({
        CardName: formData?.cardName,
        OtpToken: data.otpCode,
        PhoneNumber: getLocalPhoneNumber(formData?.phone),
        PaymentId: paymentId,
        LanguageCode: "TR",
      });

      if (!response?.Message) {
        setStep(3);
        setSelectedSavedCard?.({
          Id: response?.CardId,
          CardName: response?.CardName,
          CardNumber: response?.CardNumber,
          CardOrganizationType: response?.CardOrganizationType,
        });
        sendGTMEvent({
          event: "custom_event",
          event_category: "custom_event",
          event_action: "save_card_success",
        });
      } else {
        setServiceError(response);
      }
      sendGTMEvent({
        event: "custom_event",
        event_category: "custom_event",
        event_action: "save_card_verification",
      });
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsSubmittingStep2(false);
    }
  };

  const handleResendOTP = async () => {
    if (timer.timeLeft > 0) return;
    if (!formData?.phone) return;

    clearInlineError();
    setIsResendingOTP(true);
    try {
      const response = await pointConvertService.sendSaveCardOTP({
        PhoneNumber: getLocalPhoneNumber(formData.phone),
      });

      if (!response?.Message) {
        timer.restart();
        resetOtpForm();
      } else {
        setServiceError(response);
      }
    } catch (error) {
      setServiceError(error?.response?.data);
    } finally {
      setIsResendingOTP(false);
    }
  };

  const handleFinish = async () => {
    clearInlineError();
    setIsSubmittingStep3(true);
    try {
      await periodConversion?.();
      modal.close();
    } catch (error) {
      setServiceError(error?.response?.data || error);
    } finally {
      setIsSubmittingStep3(false);
    }
  };

  const InlineError = inlineError ? (
    <div className="text-danger border-danger/20 bg-danger/5 rounded-lg border px-4 py-3 text-sm">
      {inlineError}
    </div>
  ) : null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div
          className={clsx(
            "w-full text-xl font-semibold",
            step === 2 && "text-center",
          )}
        >
          {step === 1 && t("common.application")}
          {step === 2 && t("common.verifyOTP")}
          {step === 3 && t("common.successfullyCompleted")}
        </div>

        <button
          className="text-foreground-muted cursor-pointer text-lg"
          onClick={() => modal.close()}
          type="button"
        >
          <FontAwesomeIcon icon={byPrefixAndName.fak["x-mark-custom"]} />
        </button>
      </div>

      {step === 1 && (
        <div className="flex flex-col gap-7">
          <p className="text-foreground-muted">
            {t("common.applicationDescription")}
          </p>

          <form
            onSubmit={handleApplicationSubmit(onApplicationSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <Controller
                name="cardName"
                control={applicationControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t("common.cardName")}
                    placeholder={t("common.cardNamePlaceholder")}
                    error={applicationErrors.cardName?.message}
                    required
                  />
                )}
              />

              <Controller
                name="phone"
                control={applicationControl}
                render={({ field }) => (
                  <PhoneNumber
                    value={field.value}
                    onChange={field.onChange}
                    error={applicationErrors.phone?.message}
                    required
                  />
                )}
              />
            </div>

            {InlineError}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={isSubmittingStep1}
            >
              {t("common.save")}
            </Button>
          </form>
        </div>
      )}

      {step === 2 && (
        <div className="flex flex-col gap-7">
          <p className="text-foreground-muted text-center">
            {t("common.verifyOTPDescription")}
          </p>

          <form
            onSubmit={handleOtpSubmit(onOtpSubmit)}
            className="flex flex-col gap-6"
          >
            <div className="flex flex-col gap-4">
              <Controller
                name="otpCode"
                control={otpControl}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t("common.otpCode")}
                    placeholder={t("common.otpCodePlaceholder")}
                    error={otpErrors.otpCode?.message}
                    required
                    maxLength={6}
                    onChange={(e) => {
                      const value = e.target.value
                        .replace(/[^a-zA-Z0-9]/g, "")
                        .slice(0, 6);
                      field.onChange(value);
                    }}
                  />
                )}
              />

              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2 text-sm">
                  <span>{t("common.timeRemaining")}</span>
                  <span className="text-danger">{timer.formatTime()}</span>
                </div>

                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={timer.timeLeft > 0 || isResendingOTP}
                  className="text-primary-strong inline-flex cursor-pointer items-center gap-2 text-sm disabled:opacity-50"
                >
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["arrows-rotate-reverse"]}
                  />
                  <span>
                    {isResendingOTP
                      ? t("common.sending")
                      : t("common.resendCode")}
                  </span>
                </button>
              </div>
            </div>

            {InlineError}

            <Button
              type="submit"
              size="lg"
              className="w-full"
              loading={isSubmittingStep2}
              disabled={timer.timeLeft <= 0}
            >
              {isSubmittingStep2 ? t("common.verifying") : t("common.verify")}
            </Button>
          </form>
        </div>
      )}

      {step === 3 && (
        <div className="flex flex-col gap-5">
          <p className="text-foreground-muted">
            {t("common.successfullyCompletedDescription")}
          </p>

          <div className="border-border-weak flex items-center justify-start gap-4 rounded-lg border p-4">
            <Image
              src={`${cdnCommonPath}${selectedSavedCard?.CardOrganizationType}.svg`}
              alt={selectedSavedCard?.CardProgram}
              width={50}
              height={36}
            />
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold">
                {selectedSavedCard?.CardName}
              </span>
              <span className="text-foreground-muted text-sm">
                {selectedSavedCard?.CardNumber}
              </span>
            </div>
          </div>

          <div className="bg-background-global flex flex-col gap-4 rounded-lg px-4 py-5">
            <div className="flex items-center justify-between gap-1">
              <div className="flex flex-col gap-1 text-left">
                <span className="text-left font-semibold">
                  {t("common.monthlyConversion")}
                </span>
                <span className="text-foreground-muted text-left text-sm">
                  {t("common.monthlyConversionDescription")}
                </span>

                <button
                  type="button"
                  className="text-primary cursor-pointer text-left text-sm underline"
                  onClick={() => setShowDetails(!showDetails)}
                >
                  {showDetails
                    ? t("common.hideDetails")
                    : t("common.howItWorks")}
                </button>
              </div>
            </div>

            {showDetails && (
              <div className="text-foreground-muted scrollbar-thin scrollbar-default max-h-[200px] overflow-y-auto rounded-lg bg-gray-200 p-4 text-left text-sm">
                {t.raw("common.howItWorksDetails")}
              </div>
            )}
          </div>

          {InlineError}

          <Button
            type="button"
            size="lg"
            className="w-full"
            loading={isSubmittingStep3}
            onClick={handleFinish}
          >
            {t("common.approve")}
          </Button>
        </div>
      )}
    </div>
  );
};

const PointConvertSectionContent = () => {
  const t = useTranslationDetector();
  const { formatPrice } = usePriceFormatter();
  const { isLogin, showLoginModal } = useAuth();
  const { errorModal } = useModalHelpers();

  const saveCardModal = useModal("point-convert-save-card-modal");
  const formRef = useRef(null);

  const {
    state,
    setActiveTab,
    pointInquiryNewCard,
    setSelectedSavedCard,
    pointInquirySavedCard,
    setActiveStep,
    set3DModalOpen,
    completePointConvertPaymentNewCard,
    completePointConvertPaymentSavedCard,
    pointConvertWithNewCard,
    pointConvertWithSavedCard,
    periodConversion,
    setPaymentId,
    getBinDetails,
    getPaymentConfiguration,
  } = usePointConvert();

  const {
    activeStep,
    activeTab,
    cardProgram,
    selectedSavedCard,
    pointInfo,
    is3DModalOpen,
    iframeSrc,
    convertedPoints,
    paymentId,
    isDebitCard,
  } = state;

  const [initialFormValues, setInitialFormValues] = useState(() => {
    if (typeof window === "undefined")
      return {
        cardNumber: "",
        expiry: "",
        cvv: "",
        name: "",
      };

    try {
      const savedData = localStorage.getItem(STORAGE_KEY);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        localStorage.removeItem(STORAGE_KEY);
        return parsedData;
      }
    } catch (e) {
      console.error("Error parsing saved form data:", e);
    }

    return {
      cardNumber: "",
      expiry: "",
      cvv: "",
      name: "",
    };
  });

  const [useAllPoints, setUseAllPoints] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  const [applicationStep, setApplicationStep] = useState(1);

  const pointConvertSchema = useMemo(() => {
    const maxPoint = pointInfo?.Point ?? 0;
    return z.object({
      pointAmount: z.coerce
        .number()
        .min(0.01, t("common.pointConvertMinError"))
        .max(
          maxPoint,
          t("common.pointConvertMaxError", {
            max: formatPrice(maxPoint, CURRENCY_CODES.TRY),
          }),
        ),
    });
  }, [pointInfo?.Point, t, formatPrice]);

  const pointConvertForm = useForm({
    resolver: zodResolver(pointConvertSchema),
    defaultValues: { pointAmount: "" },
  });

  const { control, setValue, handleSubmit, formState } = pointConvertForm;
  const { errors, isSubmitting } = formState;

  // useEffect(() => {
  //   if (typeof window === "undefined") return;

  //   const savedData = localStorage.getItem(STORAGE_KEY);
  //   if (!savedData) return;

  //   try {
  //     const parsedData = JSON.parse(savedData);
  //     setInitialFormValues(parsedData);
  //   } catch (e) {
  //     console.error("Error parsing saved form data:", e);
  //   } finally {
  //     localStorage.removeItem(STORAGE_KEY);
  //   }
  // }, []);

  useEffect(() => {
    getPaymentConfiguration();
  }, []);

  useEffect(() => {
    if (useAllPoints && pointInfo?.Point != null) {
      setValue("pointAmount", String(pointInfo.Point));
    }
  }, [useAllPoints, pointInfo?.Point, setValue]);

  const handleUnmaskedLengthMatch = async (unmasked) => {
    await getBinDetails(unmasked);
  };

  const onPointConvertSubmit = async (data) => {
    if (activeTab === 0) {
      await pointConvertWithNewCard(data.pointAmount);
    } else {
      await pointConvertWithSavedCard(data.pointAmount);
    }
  };

  const onSubmit = async (data) => {
    if (!isLogin) {
      if (formRef.current?.getValues && typeof window !== "undefined") {
        const values = formRef.current.getValues();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
      }
      showLoginModal(false);
      return;
    }

    await pointInquiryNewCard(data);
  };

  useEffect(() => {
    const handleThreeDReturnEvent = async (event) => {
      if (event?.origin !== window.location.origin) return;

      const { type, payload } = event?.data || {};
      if (type !== "bank-three-d-return") return;

      if (!payload?.HasError) {
        setPaymentId(payload.PaymentId);

        if (payload.IsSavedCard === false) {
          await completePointConvertPaymentNewCard(
            { CCPointPaymentId: payload.PaymentId },
            set3DModalOpen(false),
          );
        } else {
          await completePointConvertPaymentSavedCard(
            { PaymentId: payload.PaymentId },
            set3DModalOpen(false),
          );
        }
      } else {
        errorModal({
          message: t("ErrorCodePayment.PaymentFaild"),
          isBottomSheet: true,
        });
        set3DModalOpen(false);
      }
    };

    window.addEventListener("message", handleThreeDReturnEvent);
    return () => window.removeEventListener("message", handleThreeDReturnEvent);
  }, [
    t,
    errorModal,
    setPaymentId,
    set3DModalOpen,
    completePointConvertPaymentNewCard,
    completePointConvertPaymentSavedCard,
  ]);

  return (
    <div className="flex flex-col gap-11">
      <ThreeDPaymentModal
        isOpen={is3DModalOpen}
        onClose={() => set3DModalOpen(false)}
        iframeSrc={iframeSrc}
      />

      <Modal id="point-convert-save-card-modal" showCloseButton={false}>
        <ApplicationModalContent
          selectedSavedCard={selectedSavedCard}
          modal={saveCardModal}
          step={applicationStep}
          setStep={setApplicationStep}
          t={t}
          paymentId={paymentId}
          periodConversion={periodConversion}
          setSelectedSavedCard={setSelectedSavedCard}
        />
      </Modal>

      <div className="flex items-center justify-center gap-4 font-medium">
        <div className="inline-flex items-center justify-start gap-3">
          <span
            className={clsx(
              "bg-primary border-primary-weak border-opacity-50 flex h-10 w-10 items-center justify-center rounded-full border-5 text-sm text-white",
            )}
          >
            <span>1</span>
          </span>
          <span className="text-primary flex-1">{t("common.step1")}</span>
        </div>

        <span className="flex h-full w-11 items-center justify-center">
          <span className="border-border-weak w-full border-b"></span>
        </span>

        <div className="inline-flex items-center justify-start gap-3">
          <span
            className={clsx(
              "border-opacity-50 flex h-10 w-10 items-center justify-center rounded-full border-5 text-sm",
              activeStep > 0
                ? "bg-primary border-primary-weak text-white"
                : "bg-background-500 border-border-weak text-foreground-weak",
            )}
          >
            <span>2</span>
          </span>
          <span
            className={clsx(
              "flex-1",
              activeStep > 0 ? "text-primary" : "text-foreground-weak",
            )}
          >
            {t("common.step2")}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-center gap-12 md:flex-row">
        {activeStep === 0 ? (
          <div className="border-border-weak flex w-full flex-col gap-6 rounded-lg border bg-white p-5">
            <div className="flex gap-4">
              <button
                onClick={() => setActiveTab(0)}
                className={clsx(
                  "cursor-pointer rounded-lg border px-3 py-2 leading-none transition-all",
                  activeTab === 0
                    ? "border-primary text-primary"
                    : "border-border-weak",
                )}
                type="button"
              >
                {t("common.newCard")}
              </button>
              <button
                onClick={() => setActiveTab(1)}
                className={clsx(
                  "cursor-pointer rounded-lg border px-3 py-2 leading-none transition-all",
                  activeTab === 1
                    ? "border-primary text-primary"
                    : "border-border-weak",
                )}
                type="button"
              >
                {t("common.savedCards")}
              </button>
            </div>

            <div className={clsx("tab-content", activeTab !== 0 && "hidden")}>
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-1">
                  <h5 className="text-lg font-semibold">
                    {t("common.enterCardInfo")}
                  </h5>
                  <p className="text-foreground-subtle flex items-center justify-start gap-2 text-sm">
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["circle-info"]}
                    />
                    <span>{t("common.enterCardInfoDescription")}</span>
                  </p>
                </div>

                <div className="flex flex-col gap-7">
                  <CreditCardForm
                    ref={formRef}
                    onSubmit={onSubmit}
                    scrollToForm={false}
                    onUnmaskedLengthMatch={handleUnmaskedLengthMatch}
                    cardProgramImage={cardProgram?.CardType}
                    initialValues={initialFormValues}
                    isFloating={true}
                    showCardHolder={state?.cardHolderIsRequired}
                  />

                  {isDebitCard && (
                    <div className="border-danger/25 bg-danger/5 text-danger flex items-center gap-3 rounded-xl border px-4 py-3 text-sm">
                      <FontAwesomeIcon
                        icon={byPrefixAndName.far["circle-exclamation"]}
                        className="text-danger/90 mt-0.5 h-5 w-5"
                      />
                      <div className="leading-5">
                        <div className="text-danger/80 mt-0.5 text-sm">
                          {t("common.debitCardNotSupported")}
                        </div>
                      </div>
                    </div>
                  )}

                  <Button
                    type="button"
                    size="lg"
                    className="w-full"
                    onClick={() => formRef.current?.submit()}
                    disabled={isDebitCard}
                    rightIcon={
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fal["angle-right"]}
                      />
                    }
                  >
                    {t("common.convertNewCardPoints")}
                  </Button>
                </div>
              </div>
            </div>

            <div className={clsx("tab-content", activeTab !== 1 && "hidden")}>
              {!isLogin ? (
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <h5 className="text-lg font-semibold">
                      {t("common.loginToViewSavedCards")}
                    </h5>
                    <p className="text-foreground-subtle flex items-center justify-start gap-2 text-sm">
                      <span>
                        {t("common.loginToViewSavedCardsDescription")}
                      </span>
                    </p>
                  </div>
                  <Button onClick={() => showLoginModal(false)}>
                    {t("common.login")}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-7">
                  <SavedCardsContent
                    selectedSavedCard={selectedSavedCard}
                    setSelectedSavedCard={setSelectedSavedCard}
                    t={t}
                  />

                  <Button
                    type="button"
                    size="lg"
                    className="w-full"
                    onClick={() =>
                      selectedSavedCard
                        ? pointInquirySavedCard()
                        : setActiveTab(0)
                    }
                    rightIcon={
                      <FontAwesomeIcon
                        icon={byPrefixAndName.fal["angle-right"]}
                      />
                    }
                  >
                    {t("common.convertSavedCardPoints")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : activeStep === 1 ? (
          <div className="flex w-full items-center justify-center">
            <div className="shadow-small flex w-full max-w-[580px] flex-col gap-6 rounded-lg bg-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="text-foreground-muted text-sm">
                    {t("common.convertablePoints")}
                  </span>
                  <span className="text-teal-default text-2xl font-semibold">
                    {formatPrice(pointInfo?.Point, CURRENCY_CODES.TRY)}
                  </span>
                </div>

                <div className="flex flex-col items-end">
                  <span className="text-foreground-muted text-sm">
                    {t("common.cardProgram")}
                  </span>
                  <span>
                    <Image
                      src={`${cdnCommonPath}${pointInfo?.Program}.svg`}
                      alt="card-program"
                      width={100}
                      height={100}
                    />
                  </span>
                </div>
              </div>

              <p className="text-sm">
                {t("common.pointConvertSectionDescription")}
              </p>
              <p className="text-foreground-muted flex items-center justify-start gap-2 text-sm">
                <FontAwesomeIcon icon={byPrefixAndName.fal["circle-info"]} />
                {t("common.pointConvertCannotBeUndone")}
              </p>

              <div className="border-border-weak border-b"></div>

              <form
                className="flex flex-col gap-6"
                onSubmit={handleSubmit(onPointConvertSubmit)}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex w-full items-center justify-between">
                    <span className="font-medium">
                      {t("common.pointConvertAmount")}
                    </span>
                    <div className="flex items-center gap-1">
                      <span>{t("common.pointConvertUseAll")}</span>
                      <ToggleButton
                        checked={useAllPoints}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseAllPoints(checked);
                          if (checked && pointInfo?.Point != null) {
                            setValue("pointAmount", String(pointInfo.Point));
                          }
                        }}
                      />
                    </div>
                  </div>

                  <Controller
                    name="pointAmount"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        label={t("common.pointConvertAmount") ?? "Puan miktarı"}
                        type="number"
                        step="0.01"
                        min="0.01"
                        max={pointInfo?.Point ?? undefined}
                        placeholder="0,00"
                        error={errors.pointAmount?.message}
                        onChange={(e) => {
                          const val = e.target.value;

                          if (val === "" || val === "-") {
                            field.onChange(val);
                            if (useAllPoints) setUseAllPoints(false);
                            return;
                          }

                          const normalized = val.replace(",", ".");
                          const parts = normalized.split(".");
                          if (parts.length === 2 && parts[1].length > 2) {
                            field.onChange(
                              parts[0] + "." + parts[1].slice(0, 2),
                            );
                          } else {
                            field.onChange(val);
                          }

                          if (useAllPoints) setUseAllPoints(false);
                        }}
                      />
                    )}
                  />
                </div>

                <div className="justify-en flex items-center gap-4">
                  <Button
                    size="lg"
                    type="button"
                    className="w-full"
                    onClick={() => setActiveStep(0)}
                    variant="outline"
                    color="primary"
                  >
                    {t("common.tryWithAnotherCard")}
                  </Button>

                  <Button
                    size="lg"
                    type="submit"
                    className="w-full"
                    color="primary"
                    loading={isSubmitting}
                  >
                    {t("common.pointConvert")}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="flex w-full items-center justify-center">
            <div className="shadow-small flex w-full max-w-[580px] flex-col gap-6 rounded-lg bg-white p-6">
              <div className="flex flex-col gap-7">
                <div className="flex flex-col items-center gap-2 text-center">
                  <FontAwesomeIcon
                    icon={byPrefixAndName.fak["success-icon"]}
                    className="text-teal-default text-6xl"
                  />

                  <span className="text-teal-default text-4xl font-semibold">
                    {t("common.congratulations")}
                  </span>
                  <span className="text-2xl font-semibold">
                    {t("common.yourPointsHaveBeenConverted")}
                  </span>
                  <span className="text-foreground-muted">
                    {t("common.youCanUseYourPoints")}
                  </span>

                  <div className="bg-primary-dark-muted flex w-full flex-col items-center justify-between rounded-lg">
                    <div className="bg-primary-dark-weak shadow-small flex w-full items-center justify-between gap-1 rounded-lg p-4">
                      <span>{t("common.convertedPoints")}</span>
                      <span className="text-primary font-semibold">
                        {formatPrice(
                          convertedPoints?.ConvertedPointAmount,
                          CURRENCY_CODES.TRY,
                        )}
                      </span>
                    </div>

                    {convertedPoints?.RewardAmount > 0 && (
                      <div className="flex w-full items-center justify-between gap-1 rounded-lg p-4">
                        <span>{t("common.rewardPoints")}</span>
                        <span className="text-primary font-semibold">
                          {formatPrice(
                            convertedPoints?.RewardAmount,
                            CURRENCY_CODES.TRY,
                          )}
                        </span>
                      </div>
                    )}

                    <div className="flex w-full items-center justify-between gap-1 rounded-lg p-4">
                      <span>{t("common.totalPoints")}</span>
                      <span className="text-primary font-semibold">
                        {formatPrice(
                          convertedPoints?.TotalUserPoint,
                          CURRENCY_CODES.TRY,
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-background-global rounded-lg px-4 py-5">
                  {activeTab === 0 ? (
                    <div className="flex flex-col gap-1 text-center">
                      <span className="font-semibold">
                        {t("common.wannaSaveCard")}
                      </span>
                      <span className="text-foreground-muted text-sm">
                        {t("common.wannaSaveCardDescription")}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex flex-col gap-1 text-left">
                          <span className="text-left font-semibold">
                            {t("common.monthlyConversion")}
                          </span>
                          <span className="text-foreground-muted text-left text-sm">
                            {t("common.monthlyConversionDescription")}
                          </span>

                          <button
                            type="button"
                            className="text-primary cursor-pointer text-left text-sm underline"
                            onClick={() =>
                              setIsHowItWorksOpen(!isHowItWorksOpen)
                            }
                          >
                            {isHowItWorksOpen
                              ? t("common.hideDetails")
                              : t("common.howItWorks")}
                          </button>
                        </div>

                        <ToggleButton
                          checked={periodConversion}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            if (checked) periodConversion();
                          }}
                        />
                      </div>

                      {isHowItWorksOpen && (
                        <div className="text-foreground-muted scrollbar-thin scrollbar-default max-h-[200px] overflow-y-auto rounded-lg bg-gray-200 p-4 text-left text-sm">
                          {t.raw("common.howItWorksDetails")}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between gap-4">
                  {activeTab === 0 && (
                    <Button
                      size="lg"
                      type="button"
                      className="w-full"
                      onClick={() => {
                        saveCardModal.open({
                          className: "!overflow-visible",
                          contentClassName: "!overflow-visible p-4",
                        });
                        sendGTMEvent({
                          event: "custom_event",
                          event_category: "custom_event",
                          event_action: "save_card_start",
                        });
                      }}
                      variant="outline"
                      color="primary"
                    >
                      {t("common.saveCard")}
                    </Button>
                  )}

                  <Button
                    size="lg"
                    type="button"
                    className="w-full"
                    color="primary"
                    onClick={() => setActiveStep(0)}
                  >
                    {t("common.tryWithAnotherCard")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={clsx(
            "flex w-full flex-col gap-4",
            activeStep !== 0 && "hidden",
          )}
        >
          <div className="mx-auto w-full max-w-4xl">
            <YouTubeEmbed
              videoid="lBTtqU_k_ss"
              style="border-radius: 0.5rem;"
            />
          </div>
          <p className="text-foreground-subtle text-center">
            {t("common.pointConvertSectionDescription")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-4 py-7">
        <Image
          src={`${cdnCommonPath}MAXIMUM.svg`}
          alt="point-convert-section-1"
          width={100}
          height={100}
        />
        <Image
          src={`${cdnCommonPath}WORLD.svg`}
          alt="point-convert-section-1"
          width={100}
          height={100}
        />
        <Image
          src={`${cdnCommonPath}BONUS.svg`}
          alt="point-convert-section-1"
          width={100}
          height={100}
        />
        <Image
          src={`${cdnCommonPath}AXESS.svg`}
          alt="point-convert-section-1"
          width={100}
          height={100}
        />
        <Image
          src={`${cdnCommonPath}PARAF.svg`}
          alt="point-convert-section-1"
          width={100}
          height={100}
        />
        <Image
          src={`${cdnCommonPath}COMBO.svg`}
          alt="point-convert-section-1"
          width={100}
          height={100}
        />
      </div>
    </div>
  );
};

const PointConvertSection = () => (
  <PointConvertProvider>
    <PointConvertSectionContent />
  </PointConvertProvider>
);

export default PointConvertSection;
